import { Box, Button, CircularProgress, Divider, Grid, IconButton } from '@mui/material';
import campaignHeaderBg from '../../assets/campaign-header-background.png';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WalletOutlinedIcon from '@mui/icons-material/WalletOutlined';
import AddIcon from '@mui/icons-material/Add';
import { Trans, useTranslation } from 'react-i18next';
import CampaignInfoExpand from './CampaignInfoExpand';
import { ISmsPanelContent, IViewCampaign, Social } from '../../interfaces/campaign';
import { getMediaLogo } from '../../utils/file';
import FinishedCampaignExpand from './FinishedCampaignExpand';
import RejectedCampaignExpand from './RejectedCampaignExpand';
import { useCampaignStatusColors } from '../../hooks/useCampaignStatusColors';
import { FC, useContext, useEffect, useState } from 'react';
import { formatNumberWithComma } from '../../utils/number';
import { CampaignContext } from '../../contexts/CampaignContext';
import { useParams, useSearchParams } from 'react-router-dom';
import IResponse from '../../interfaces/response';
import axios from 'axios';
import { ShareNavigator } from '../common/shareFunction';
import CreateCampaignBox from './CreateCampaignBox';
import PaymentModal from '../finance/PaymentModal';
import NotSocialMedia from './NotSocialMedia';
import IsSocialPreview from './IsSocialPreview';

interface Props {
	is_Admin?: boolean;
}

const ViewCampaign: FC<Props> = ({ is_Admin = false }) => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const [editAction, setEditAction] = useState(true);
	const [showBox] = useState(true);
	const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
	const [smsContent, setSmsContent] = useState<ISmsPanelContent | null>(null);
	const { loadingCampaign, fetchedCampaign, fetchedMedias, selectedPlatformId, resetAllFields, setSelectedPackages, setSelectedPages, setSelectedCampaignId, setTitle, setSelectedPlatformId, setSelectedDate, setSelectedPlatform, setSelectedMediaTypeId, setSelectedTime, setContentStatus, setDescription, setSelectedCategoryId, setSelectedPurpose, setFetchedCampaign, setLoadingCampaign } = useContext(CampaignContext);

	const [searchParams] = useSearchParams();
	const action_view = searchParams.get('action');
	const type_view = searchParams.get('type');
	const filterWithType = fetchedMedias?.find(item => item.id === selectedPlatformId);
	const platform = fetchedCampaign?.media! as Social;

	const bgColorClass = useCampaignStatusColors(fetchedCampaign?.status!);
	const campaignPrice = formatNumberWithComma(fetchedCampaign?.factor_price!);
	const showAddCampaignButton = fetchedCampaign?.status === 'completed' || fetchedCampaign?.status === 'rejected';
	const isAdvertising = fetchedCampaign?.campaign_type === 'advertising';
	const handleClosePaymentModal = () => {
		setIsPaymentModalOpen(false);
	};

	const handleOpenPaymentModal = () => {
		setIsPaymentModalOpen(true);
	};

	useEffect(() => {
		setSelectedCampaignId(id!);
	}, [id]);

	const handleEditAction = () => {
		setEditAction(!editAction);
	};
	// const handleOpenBox = () => {
	// 	setShowBox(!showBox);
	// };

	useEffect(() => {
		if (action_view === 'edit') {
			setEditAction(true);
		} else if (action_view !== 'edit') {
			setEditAction(false);
		}

		if (!id) {
			return;
		}

		setLoadingCampaign(true);
		axios<IResponse<IViewCampaign>>({
			url: `${type_view === 'admin' ? '/admin' : ''}/campaigns/${id}`,
		})
			.then(res => {
				if (res.data.success) {
					const purposeId = res.data.data.purpose_id;
					if (typeof purposeId === 'number') {
						setSelectedPurpose({ [purposeId.toString()]: true });
					}
					setFetchedCampaign(res.data.data);
					setSelectedCategoryId(res.data.data.category_id);
					setTitle(res.data.data.title);
					setDescription(res.data.data.description);
					setContentStatus(res.data.data.content_status);
					setSelectedPlatformId(res.data.data.media_id);
					setSelectedPlatform(res.data.data.media);
					setSelectedMediaTypeId(res.data.data.media_type_id);
					setSelectedPackages(res.data.data.packages);
					setSelectedPages(res.data.data.entities);
					setSelectedDate(res.data.data.release_date ?? new Date());
					setSelectedTime(res.data.data.release_time_id);
					setSmsContent(selectedPlatformId === 8 ? res.data?.data?.sms_contents[0] : res.data?.data?.contents[0]);
				}
			})
			.finally(() => {
				setLoadingCampaign(false);
			});
		return () => {
			setFetchedCampaign(undefined);
			resetAllFields();
		};
	}, []);
	return (
		<>
			<PaymentModal rout={'/campaigns/pay-multiple'} amount={Number(fetchedCampaign?.price)} enableType={true} data={{ campaigns: [id] }} open={isPaymentModalOpen} onClose={handleClosePaymentModal} />
			<Box className="mb-14 flex flex-col">
				{loadingCampaign ? (
					<Box className="flex min-h-96 w-full items-center justify-center">
						<CircularProgress />
					</Box>
				) : (
					<>
						<Grid container className="flex justify-between rounded-lg bg-background-paper p-6">
							<Grid item className="flex">
								<Box className="relative">
									<img src={campaignHeaderBg} alt="campaign header bg" className="h-48 w-48" />
									<img src={getMediaLogo(platform)} alt={`${platform} logo`} className="absolute right-0 top-2 h-12 w-12" />
								</Box>
								<Box className="me-8 ms-6 flex flex-col justify-between text-base text-text-secondary">
									<span>{t('campaign_id')} :</span>
									<span>{t('campaign_name')} :</span>
									<span>{t('release-platform')} :</span>
									<span>{t('content_type')} :</span>
									<span>{t('status')} :</span>
								</Box>
								<Box className="flex flex-col justify-between text-sm font-medium text-text-primary">
									<span>{fetchedCampaign?.id}</span>
									<span>{fetchedCampaign?.title}</span>
									<span>{t(fetchedCampaign?.media!)}</span>
									<span>{t(fetchedCampaign?.content_type!)}</span>
									<span className={`rounded-md px-3 py-1 text-xs ${bgColorClass}`}>{t(fetchedCampaign?.status!)}</span>
								</Box>
								<Divider orientation="vertical" sx={{ borderRightWidth: 2 }} className="mx-8" />
								<Box className="me-12 ms-6 flex flex-col gap-y-6 text-base text-text-secondary">
									<span>{t('user_name_creator')} :</span>
									<span>{t('phone')} :</span>
								</Box>
								<Box className="flex flex-col gap-y-6 text-sm font-medium text-text-primary">
									<span>
										{fetchedCampaign?.user?.first_name} {fetchedCampaign?.user?.last_name}
									</span>
									<span>{fetchedCampaign?.user?.phone}</span>
								</Box>
							</Grid>
							<Grid item className="flex grow flex-col items-end justify-between">
								<Box className="flex items-center gap-x-2">
									<ShareNavigator url="https://app.maino.ir" text={t('site_name')} title={t('site_name')} />
									{(fetchedCampaign?.status === 'rejected' || fetchedCampaign?.status === 'pending' || fetchedCampaign?.status === 'payment_pend') && (
										<IconButton onClick={handleEditAction}>
											<EditOutlinedIcon className="h-7 w-7 text-text-disabled" />
										</IconButton>
									)}
									{/* <IconButton onClick={handleOpenBox}>
										<InfoOutlinedIcon className="h-7 w-7 text-text-disabled" />
									</IconButton> */}
								</Box>
								{fetchedCampaign?.status === 'payment_pend' ? (
									<Box className="my-2 flex flex-col gap-y-2">
										<Button variant="outlined" className="px-5" onClick={() => handleOpenPaymentModal()} size="large">
											<Trans i18nKey="campaign-price" values={{ amount: campaignPrice }}></Trans>
										</Button>
										{is_Admin && (
											<Button variant="contained" className="px-5" size="large" startIcon={showAddCampaignButton ? <AddIcon /> : <WalletOutlinedIcon />}>
												{showAddCampaignButton ? t('create_new_campaign') : t('activate_campaign')}
											</Button>
										)}
									</Box>
								) : (
									<Box className="my-2 flex flex-col gap-y-2">
										{(fetchedCampaign?.status === 'rejected' || fetchedCampaign?.status === 'pending') && (
											<Button variant="outlined" className="w-64 px-3" size="large">
												{t('for_edit_data_you_sholde_click_on_edit_icon')}
											</Button>
										)}

										<Button variant="outlined" className="w-64 px-5" size="large">
											{t('pend_payment_campaign')}
										</Button>
									</Box>
								)}
							</Grid>
						</Grid>
						{fetchedCampaign?.status === 'completed' && <FinishedCampaignExpand />}
						{fetchedCampaign?.status === 'rejected' && <RejectedCampaignExpand />}
						{fetchedCampaign?.status === 'rejected' || fetchedCampaign?.status === 'pending' ? (
							<>
								<CreateCampaignBox completedCampaign={editAction ? false : true} campaignType={isAdvertising ? t('advertising') : t('reportage')} campaignId={id} />
							</>
						) : !editAction ? (
							<>
								<CampaignInfoExpand showExpanded={showBox} />
								{filterWithType?.is_social ? (
									<IsSocialPreview />
								) : (
									<Box className="rounded-md bg-white p-3">
										<NotSocialMedia openNotSocialPreview={true} smsContent={smsContent} previewContent={true} />
									</Box>
								)}
							</>
						) : (
							<>
								<CampaignInfoExpand showExpanded={showBox} editHeader={true} action={editAction ? 'create' : 'read'} />
								{filterWithType?.is_social ? (
									<IsSocialPreview />
								) : (
									<Box className="rounded-md bg-white p-3">
										<NotSocialMedia openNotSocialPreview={true} smsContent={smsContent} previewContent={true} />
									</Box>
								)}
							</>
						)}
					</>
				)}
			</Box>
		</>
	);
};

export default ViewCampaign;
